import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { searchApi, SearchParams } from '../api/search';
import ProductList from '../components/home/ProductList';
import Pagination from '../components/main/Pagination';
import PageHeader from '../components/main/PageHeader';
import SearchSortFilterBar from '../components/main/SearchSortFilterBar';
import type { Product } from '../types/product';
import { useBrands } from '../contexts/BrandContext';
import { enhanceProductsWithBrands } from '../utils/brandUtils';
import { useTranslation } from 'react-i18next';

export default function Search() {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { brands } = useBrands();
  const { t } = useTranslation();

  const [rawProducts, setRawProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'relevance' | 'new' | 'popular' | 'price_low' | 'price_high'>('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = isMobile ? 12 : 20; // Responsive limit: 12 for mobile, 20 for PC

  // Get search query from URL params
  const queryFromUrl = searchParams.get('q') || '';

  const fetchSearchResults = useCallback(async (query: string, page: number, sort: 'relevance' | 'new' | 'popular' | 'price_low' | 'price_high') => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const params: SearchParams = {
        q: query.trim(),
        page,
        sortBy: sort,
        limit,
      };
      
      const response = await searchApi.searchProducts(params);
      
      if (response.success) {
        setRawProducts(response.products);
        setTotalPages(response.pagination.totalPages);
        setTotalItems(response.pagination.totalCount);
        setCurrentPage(response.pagination.page);
        
        // Update sortBy if it's different from the API response
        if (response.query.sortBy && response.query.sortBy !== sortBy) {
          const validSortBy = ['relevance', 'new', 'popular', 'price_low', 'price_high'].includes(response.query.sortBy) 
            ? response.query.sortBy as 'relevance' | 'new' | 'popular' | 'price_low' | 'price_high'
            : 'relevance';
          setSortBy(validSortBy);
        }
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setRawProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [limit, sortBy]);

  // Enhance products with brand names using cached brand data
  const products = useMemo(() => {
    if (rawProducts.length > 0 && brands.length > 0) {
      return enhanceProductsWithBrands(rawProducts, brands);
    }
    return rawProducts;
  }, [rawProducts, brands]);

  useEffect(() => {
    // Initialize search query from URL
    if (queryFromUrl) {
      setCurrentPage(1);
      fetchSearchResults(queryFromUrl, 1, sortBy);
    }
  }, [queryFromUrl, sortBy, fetchSearchResults]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);



  const handleSortChange = (newSortBy: 'relevance' | 'new' | 'popular' | 'price_low' | 'price_high') => {
    setSortBy(newSortBy);
    if (queryFromUrl) {
      fetchSearchResults(queryFromUrl, currentPage, newSortBy);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (queryFromUrl) {
      fetchSearchResults(queryFromUrl, page, sortBy);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 4,
      }}
    >
      {/* Header */}
      <PageHeader title={t('common.search')} />



      {/* Search Results Header */}
      {queryFromUrl && (
        <Container maxWidth="lg">
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ color: theme.palette.custom.brown, mb: 1 }}>
              {t('search.resultsFor')}: "{queryFromUrl}"
            </Typography>
          </Box>
          
          <SearchSortFilterBar
            totalItems={totalItems}
            sortBy={sortBy}
            onSortChange={handleSortChange}
          />
        </Container>
      )}

      {/* Search Results */}
      {queryFromUrl ? (
        <>
          {/* Products List */}
          <ProductList
            products={products}
            loading={loading}
            variant="grid"
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        /* Empty State */
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('search.startSearching')}
            </Typography>
            <Typography color="text.secondary">
              {t('search.enterKeywords')}
            </Typography>
          </Box>
        </Container>
      )}
    </Box>
  );
}
